import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Activity,
  Brain,
  MessageSquare,
  Settings,
  CheckCircle2,
  TrendingUp,
  Server,
  Eye,
  Shield,
  RefreshCw,
  Users,
} from 'lucide-react';
import { Card } from '../ui/Card'
import { Badge } from '../ui/Badge'
import { Button } from '../ui/Button'
import { cn } from '../../utils/cn'
import { formatRelativeTime } from '../../utils/format'
import { MCPSecurityGateway } from './MCPSecurityGateway';
import { DistributedAgentOrchestration } from './DistributedAgentOrchestration';
import { ComplianceMonitor } from './ComplianceMonitor';

interface MCPServerMetrics {
  id: string;
  name: string;
  status: 'running' | 'stopped' | 'error' | 'starting';
  uptime: number;
  requestCount: number;
  errorRate: number;
  responseTime: number;
  memoryUsage: number;
  cpuUsage: number;
  connections: number;
  version: string;
  lastActivity: Date;
  capabilities: string[];
  riskLevel: 'low' | 'medium' | 'high' | 'blocked';
  deployment: 'self-hosted' | 'aws-bedrock' | 'azure-openai' | 'public-api' | 'overseas';
  region?: string;
}

interface AIModelMetrics {
  id: string;
  name: string;
  provider: string;
  status: 'active' | 'idle' | 'error' | 'loading';
  requestsPerMinute: number;
  tokensProcessed: number;
  averageLatency: number;
  errorRate: number;
  costPerRequest: number;
  temperature: number;
  maxTokens: number;
  lastUsed: Date;
  riskLevel: 'low' | 'medium' | 'high' | 'blocked';
  deployment: 'self-hosted' | 'aws-bedrock' | 'azure-openai' | 'public-api' | 'overseas';
  region?: string;
  dataResidency: string;
}

// Empty data -- wire to API when backend is available
const generateMockMCPServers = (): MCPServerMetrics[] => [];

const generateMockAIModels = (): AIModelMetrics[] => [];

// Risk level utilities
const getRiskBadgeVariant = (riskLevel: string) => {
  switch (riskLevel) {
    case 'low': return 'success';
    case 'medium': return 'warning';
    case 'high': return 'danger';
    case 'blocked': return 'danger';
    default: return 'secondary';
  }
};

const getRiskDescription = (riskLevel: string) => {
  switch (riskLevel) {
    case 'low': return 'Self-hosted - Full control over data and processing';
    case 'medium': return 'Hosted provider (AWS/GCP) - Controlled environment with compliance';
    case 'high': return 'Public API - Data sent to external servers';
    case 'blocked': return 'Overseas/Unknown - Blocked by security policy';
    default: return 'Unknown risk level';
  }
};

export const AIMCPDashboard: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get initial tab from URL or default to 'overview'
  const getInitialTab = () => {
    const tab = searchParams.get('tab');
    const validTabs = ['overview', 'orchestration', 'security', 'compliance', 'mcp-servers', 'ai-models', 'logs'];
    if (tab && validTabs.includes(tab)) {
      return tab as any;
    }
    return 'overview';
  };
  
  const [activeTab, setActiveTab] = useState<'overview' | 'orchestration' | 'security' | 'compliance' | 'mcp-servers' | 'ai-models' | 'logs'>(getInitialTab());
  
  // Update URL when tab changes
  const handleTabChange = useCallback((tab: string) => {
    setActiveTab(tab as any);
    setSearchParams({ tab });
  }, [setSearchParams]);
  
  // Sync with URL changes
  useEffect(() => {
    const tab = searchParams.get('tab');
    const validTabs = ['overview', 'orchestration', 'security', 'compliance', 'mcp-servers', 'ai-models', 'logs'];
    if (tab && validTabs.includes(tab) && tab !== activeTab) {
      setActiveTab(tab as any);
    }
  }, [searchParams, activeTab]);

  const mcpServers = generateMockMCPServers();
  const aiModels = generateMockAIModels();

  const overviewMetrics = useMemo(() => {
    const totalMCPRequests = mcpServers.reduce((sum, server) => sum + server.requestCount, 0);
    const avgMCPResponseTime = mcpServers.reduce((sum, server) => sum + server.responseTime, 0) / mcpServers.length;
    const activeMCPServers = mcpServers.filter(s => s.status === 'running').length;
    const totalAIRequests = aiModels.reduce((sum, model) => sum + model.tokensProcessed, 0);
    const activeAIModels = aiModels.filter(m => m.status === 'active').length;
    const avgAICost = aiModels.reduce((sum, model) => sum + model.costPerRequest, 0) / aiModels.length;

    // Risk-based metrics
    const lowRiskMCP = mcpServers.filter(s => s.riskLevel === 'low').length;
    const mediumRiskMCP = mcpServers.filter(s => s.riskLevel === 'medium').length;
    const highRiskMCP = mcpServers.filter(s => s.riskLevel === 'high').length;
    const blockedMCP = mcpServers.filter(s => s.riskLevel === 'blocked').length;

    const lowRiskAI = aiModels.filter(m => m.riskLevel === 'low').length;
    const mediumRiskAI = aiModels.filter(m => m.riskLevel === 'medium').length;
    const highRiskAI = aiModels.filter(m => m.riskLevel === 'high').length;
    const blockedAI = aiModels.filter(m => m.riskLevel === 'blocked').length;

    return {
      totalMCPRequests,
      avgMCPResponseTime: Math.round(avgMCPResponseTime),
      activeMCPServers,
      totalMCPServers: mcpServers.length,
      totalAIRequests,
      activeAIModels,
      totalAIModels: aiModels.length,
      avgAICost: avgAICost.toFixed(3),
      riskBreakdown: {
        mcp: { low: lowRiskMCP, medium: mediumRiskMCP, high: highRiskMCP, blocked: blockedMCP },
        ai: { low: lowRiskAI, medium: mediumRiskAI, high: highRiskAI, blocked: blockedAI },
      },
    };
  }, [mcpServers, aiModels]);

  const renderOverview = () => (
    <div className="bg-gray-800 rounded-lg p-6">
      <div className="space-y-6">
      {/* System Status Cards */}
      <div className="dashboard-grid">
        <Card className="bg-gray-900">
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl suse-bold text-console-text">{overviewMetrics.activeMCPServers}/{overviewMetrics.totalMCPServers}</div>
                <div className="text-sm text-console-muted">MCP Servers Online</div>
              </div>
              <Server className="w-8 h-8 text-success-500" />
            </div>
          </div>
        </Card>

        <Card className="bg-gray-900">
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl suse-bold text-console-text">{overviewMetrics.activeAIModels}/{overviewMetrics.totalAIModels}</div>
                <div className="text-sm text-console-muted">AI Models Active</div>
              </div>
              <Brain className="w-8 h-8 text-primary-500" />
            </div>
          </div>
        </Card>

        <Card className="bg-gray-900">
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl suse-bold text-console-text">{overviewMetrics.totalMCPRequests.toLocaleString()}</div>
                <div className="text-sm text-console-muted">MCP Requests</div>
              </div>
              <Activity className="w-8 h-8 text-warning-500" />
            </div>
          </div>
        </Card>

        <Card className="bg-gray-900">
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl suse-bold text-console-text">${overviewMetrics.avgAICost}</div>
                <div className="text-sm text-console-muted">Avg AI Cost/Request</div>
              </div>
              <TrendingUp className="w-8 h-8 text-accent-500" />
            </div>
          </div>
        </Card>
      </div>

      {/* Risk Breakdown */}
      <div className="feature-grid">
        <Card className="bg-gray-900">
          <div className="p-6">
            <h3 className="text-lg suse-semibold text-console-text mb-4">MCP Server Risk Distribution</h3>
            <div className="space-y-3">
              {['blocked', 'high', 'medium', 'low'].map(risk => {
                const count = overviewMetrics.riskBreakdown.mcp[risk as keyof typeof overviewMetrics.riskBreakdown.mcp];
                return (
                  <div key={risk} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant={getRiskBadgeVariant(risk)} className="w-16 text-xs justify-center">
                        {risk}
                      </Badge>
                      <span className="text-sm text-console-muted capitalize">{risk} Risk</span>
                    </div>
                    <span className="text-console-text font-semibold">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>

        <Card className="bg-gray-900">
          <div className="p-6">
            <h3 className="text-lg suse-semibold text-console-text mb-4">AI Model Risk Distribution</h3>
            <div className="space-y-3">
              {['blocked', 'high', 'medium', 'low'].map(risk => {
                const count = overviewMetrics.riskBreakdown.ai[risk as keyof typeof overviewMetrics.riskBreakdown.ai];
                return (
                  <div key={risk} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant={getRiskBadgeVariant(risk)} className="w-16 text-xs justify-center">
                        {risk}
                      </Badge>
                      <span className="text-sm text-console-muted capitalize">{risk} Risk</span>
                    </div>
                    <span className="text-console-text font-semibold">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="bg-gray-900">
        <div className="p-6">
          <h3 className="text-lg suse-semibold text-console-text mb-4">Quick Actions</h3>
          <div className="flex flex-wrap gap-3">
            <Button variant="primary" size="sm" className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4" />
              Refresh All Services
            </Button>
            <Button variant="secondary" size="sm" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Configure MCP
            </Button>
            <Button variant="secondary" size="sm" className="flex items-center gap-2">
              <Brain className="w-4 h-4" />
              Model Settings
            </Button>
            <Button variant="secondary" size="sm" className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Security Policies
            </Button>
            <Button variant="secondary" size="sm" className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              View Logs
            </Button>
          </div>
        </div>
      </Card>
      </div>
    </div>
  );

  const renderMCPServers = () => {
    // Group servers by risk level
    const serversByRisk = mcpServers.reduce((acc, server) => {
      if (!acc[server.riskLevel]) {
acc[server.riskLevel] = [];
}
      acc[server.riskLevel].push(server);
      return acc;
    }, {} as Record<string, typeof mcpServers>);

    return (
      <div className="bg-gray-800 rounded-lg p-6">
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg suse-semibold text-console-text">MCP Server Status</h3>
          <Button variant="primary" size="sm" className="flex items-center gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh Status
          </Button>
        </div>

        {['blocked', 'high', 'medium', 'low'].map(riskLevel => {
          const servers = serversByRisk[riskLevel] || [];
          if (servers.length === 0) {
return null;
}

          return (
            <div key={riskLevel} className="space-y-4">
              <div className="flex items-center gap-3">
                <h4 className="text-md suse-semibold text-console-text capitalize">{riskLevel} Risk</h4>
                <Badge variant={getRiskBadgeVariant(riskLevel)} className="text-xs">
                  {servers.length} server{servers.length !== 1 ? 's' : ''}
                </Badge>
              </div>
              
              <div className="grid gap-4">
                {servers.map((server) => (
                  <Card key={server.id} className="bg-gray-900">
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            'w-3 h-3 rounded-full',
                            server.status === 'running' ? 'bg-success-500' :
                            server.status === 'error' ? 'bg-danger-500' :
                            server.status === 'stopped' ? 'bg-gray-500' :
                            'bg-warning-500',
                          )} />
                          <div>
                            <h4 className="suse-semibold text-console-text">{server.name}</h4>
                            <p className="text-sm text-console-muted">v{server.version} • {server.deployment} • {server.region}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={getRiskBadgeVariant(server.riskLevel)}>
                            {server.riskLevel}
                          </Badge>
                          <Badge variant={server.status === 'running' ? 'success' : server.status === 'error' ? 'danger' : 'secondary'}>
                            {server.status}
                          </Badge>
                          <Button variant="ghost" size="sm">
                            <Settings className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      <div className="text-xs text-console-muted mb-3 p-2 bg-gray-900 rounded">
                        {getRiskDescription(server.riskLevel)}
                      </div>

                      <div className="metrics-grid mb-4">
                        <div>
                          <div className="text-sm text-console-muted">Requests</div>
                          <div className="text-lg suse-semibold text-console-text">{server.requestCount.toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="text-sm text-console-muted">Response Time</div>
                          <div className="text-lg suse-semibold text-console-text">{server.responseTime}ms</div>
                        </div>
                        <div>
                          <div className="text-sm text-console-muted">Error Rate</div>
                          <div className="text-lg suse-semibold text-console-text">{server.errorRate}%</div>
                        </div>
                        <div>
                          <div className="text-sm text-console-muted">Memory</div>
                          <div className="text-lg suse-semibold text-console-text">{server.memoryUsage}%</div>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {server.capabilities.map((capability) => (
                          <Badge key={capability} variant="secondary" className="text-xs">
                            {capability}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          );
        })}
        </div>
      </div>
    );
  };

  const renderAIModels = () => {
    // Group models by risk level
    const modelsByRisk = aiModels.reduce((acc, model) => {
      if (!acc[model.riskLevel]) {
acc[model.riskLevel] = [];
}
      acc[model.riskLevel].push(model);
      return acc;
    }, {} as Record<string, typeof aiModels>);

    return (
      <div className="bg-gray-800 rounded-lg p-6">
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg suse-semibold text-console-text">AI Model Status</h3>
          <Button variant="primary" size="sm" className="flex items-center gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh Models
          </Button>
        </div>

        {['blocked', 'high', 'medium', 'low'].map(riskLevel => {
          const models = modelsByRisk[riskLevel] || [];
          if (models.length === 0) {
return null;
}

          return (
            <div key={riskLevel} className="space-y-4">
              <div className="flex items-center gap-3">
                <h4 className="text-md suse-semibold text-console-text capitalize">{riskLevel} Risk</h4>
                <Badge variant={getRiskBadgeVariant(riskLevel)} className="text-xs">
                  {models.length} model{models.length !== 1 ? 's' : ''}
                </Badge>
              </div>
              
              <div className="grid gap-4">
                {models.map((model) => (
                  <Card key={model.id} className="bg-gray-900">
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            'w-3 h-3 rounded-full',
                            model.status === 'active' ? 'bg-success-500' :
                            model.status === 'error' ? 'bg-danger-500' :
                            model.status === 'idle' ? 'bg-gray-500' :
                            'bg-warning-500',
                          )} />
                          <div>
                            <h4 className="suse-semibold text-console-text">{model.name}</h4>
                            <p className="text-sm text-console-muted">{model.provider} • {model.deployment} • {model.region}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={getRiskBadgeVariant(model.riskLevel)}>
                            {model.riskLevel}
                          </Badge>
                          <Badge variant={model.status === 'active' ? 'success' : model.status === 'error' ? 'danger' : 'secondary'}>
                            {model.status}
                          </Badge>
                          <Button variant="ghost" size="sm">
                            <Settings className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      <div className="text-xs text-console-muted mb-3 p-2 bg-gray-900 rounded">
                        {getRiskDescription(model.riskLevel)} • Data Residency: {model.dataResidency}
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                        <div>
                          <div className="text-sm text-console-muted">Tokens Processed</div>
                          <div className="text-lg suse-semibold text-console-text">{model.tokensProcessed.toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="text-sm text-console-muted">Avg Latency</div>
                          <div className="text-lg suse-semibold text-console-text">{model.averageLatency}ms</div>
                        </div>
                        <div>
                          <div className="text-sm text-console-muted">Error Rate</div>
                          <div className="text-lg suse-semibold text-console-text">{model.errorRate}%</div>
                        </div>
                        <div>
                          <div className="text-sm text-console-muted">Cost/Request</div>
                          <div className="text-lg suse-semibold text-console-text">${model.costPerRequest}</div>
                        </div>
                        <div>
                          <div className="text-sm text-console-muted">Last Used</div>
                          <div className="text-lg suse-semibold text-console-text">{formatRelativeTime(model.lastUsed)}</div>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          );
        })}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl suse-bold text-console-text">AI & MCP Management</h1>
          <p className="text-console-muted mt-2">Monitor and manage AI models and Model Context Protocol servers</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="success" className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            All Systems Operational
          </Badge>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-8 p-1 bg-gray-800 rounded-lg w-fit">
        {[
          { id: 'overview', label: 'Overview', icon: Activity },
          { id: 'orchestration', label: 'Agent Orchestration', icon: Users },
          { id: 'security', label: 'MCP Security', icon: Shield },
          { id: 'compliance', label: 'Compliance', icon: CheckCircle2 },
          { id: 'mcp-servers', label: 'MCP Servers', icon: Server },
          { id: 'ai-models', label: 'AI Models', icon: Brain },
          { id: 'logs', label: 'Logs', icon: MessageSquare },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-md transition-all duration-200 text-sm suse-medium',
              activeTab === tab.id
                ? 'bg-primary-600 text-white shadow-lg'
                : 'text-console-muted hover:text-console-text hover:bg-secondary-800/50',
            )}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && renderOverview()}
      {activeTab === 'orchestration' && <DistributedAgentOrchestration />}
      {activeTab === 'security' && <MCPSecurityGateway />}
      {activeTab === 'compliance' && <ComplianceMonitor />}
      {activeTab === 'mcp-servers' && renderMCPServers()}
      {activeTab === 'ai-models' && renderAIModels()}
      {activeTab === 'logs' && (
        <Card className="bg-gray-900">
          <div className="p-6 text-center">
            <MessageSquare className="w-12 h-12 text-console-muted mx-auto mb-4" />
            <h3 className="text-lg suse-semibold text-console-text mb-2">Logs View</h3>
            <p className="text-console-muted">Real-time logs and debugging information will be available here.</p>
          </div>
        </Card>
      )}
    </>
  );
};

export default AIMCPDashboard;